import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import sh from "bundles/common/utils/sh";
import NewJobOfferCard from "bundles/Candidate/components/NewJobOfferCard";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";

const JobOffersSection = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale) || "en";

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchJobs = () => {
    setLoading(true);
    sh.get("random_jobs", {
      params: {
        quantity: 6,
        locale: i18nLocale,
        authenticity_token: authenticityToken,
      },
    })
      .then((res) => {
        setOffers(res.data?.data || []);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchJobs();
  }, [authenticityToken, i18nLocale]);

  const humanizeSlug = (value) =>
    (value || "")
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const handleOfferClick = (offer) => {
    if (!offer?.id) return;

    const offerCategorySlug = Array.isArray(offer?.categories) && offer.categories.length > 0
      ? offer.categories[0]
      : null;
    const offerIndustry = Array.isArray(offer?.company_industries) && offer.company_industries.length > 0
      ? offer.company_industries[0]
      : null;

    const navJobOffersDefaultMessage =
      i18nLocale === "fr"
        ? "Offres d'emploi"
        : i18nLocale === "nl"
          ? "Jobaanbiedingen"
          : i18nLocale === "de"
            ? "Stellenangebote"
            : "Job Offers";

    const crumbs = [
      { label: intl.formatMessage({ id: "menu.home", defaultMessage: "Home" }), path: `/${i18nLocale}` },
      {
        label: intl.formatMessage({ id: "nav.job_offers", defaultMessage: navJobOffersDefaultMessage }),
        path: `/${i18nLocale}/job-offers`,
      },
    ];

    if (offerIndustry) {
      crumbs.push({
        label: intl.formatMessage({
          id: `sectors.${offerIndustry}`,
          defaultMessage: humanizeSlug(offerIndustry),
        }),
        path: `/${i18nLocale}/job-offers/${offerIndustry}`,
      });
    }

    if (offerIndustry && offerCategorySlug) {
      crumbs.push({
        label: intl.formatMessage({
          id: `categories.${offerCategorySlug}`,
          defaultMessage: humanizeSlug(offerCategorySlug),
        }),
        path: `/${i18nLocale}/job-offers/${offerIndustry}/${offerCategorySlug}`,
      });
    }

    navigate(`/${i18nLocale}/job-offers/${offer.id}`, { state: { crumbs } });
  };

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: "1rem", md: "1.5rem" },
        pb: { xs: "2rem", md: "3rem" },
        px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        bgcolor: "hsl(160 10% 96% / 0.4)",
      }}
    >
      <Box sx={{ maxWidth: "80rem", mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontFamily: "Segoe UI Black, sans-serif, system-ui",
              color: "hsl(178 93% 10%)",
              letterSpacing: "0.02em",
              fontSize: { xs: "1rem", md: "1.2rem" },
              textAlign: "left",
            }}
          >
            <FormattedMessage id="landing_candidate.curated_opportunities" />
          </Typography>

          <Button
            onClick={() => (window.location.href = `/${i18nLocale}/job-offers`)}
            disableRipple
            sx={{
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              fontSize: "0.875rem",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(153 68% 25%)",
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
            }}
          >
            <FormattedMessage id="landing_candidate.see_all" />{" "}
            {"\u2192"}
          </Button>
        </Box>

        {loading && offers.length === 0 ? (
          <SquarehubLoader size={72} centered />
        ) : (
          <Grid container spacing={2}>
            {offers.map((offer, index) => (
              <Grid item xs={12} md={6} key={offer.id} sx={{ display: "flex" }}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  sx={{ width: "100%", display: "flex", minHeight: 0 }}
                >
                  <NewJobOfferCard offer={offer} onClick={handleOfferClick} />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          sx={{ mt: 4, display: "flex", justifyContent: "center" }}
        >
          <Button
            component={motion.button}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            onClick={() => (window.location.href = `/${i18nLocale}/job-offers`)}
            sx={{
              borderRadius: "0.75rem",
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "0.875rem",
              color: "#FFF",
              backgroundColor: "hsl(153 68% 25%)",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              "&:hover": { backgroundColor: "hsl(153 68% 25% / 0.9)" },
            }}
          >
            <FormattedMessage id="landing_candidate.discover_all_opportunities" />
            <ArrowRight size={16} />
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default JobOffersSection;
